import {
	type OrderShipmentStatus,
	useGetShippingCarriersQuery,
	useUpdateOrderShipmentTrackingMutation,
} from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { DatePicker } from '../ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { Field } from '../ui/field';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

/** How each field the API can ask for is labelled and entered. */
const FIELD_META: Record<string, { label: string; kind: 'text' | 'date' }> = {
	carrier: { label: 'Carrier', kind: 'text' },
	trackingNumber: { label: 'Tracking number', kind: 'text' },
	shippedAt: { label: 'Shipped', kind: 'date' },
	estimatedDeliveryDate: { label: 'Est. delivery', kind: 'date' },
	actualDeliveryDate: { label: 'Delivered', kind: 'date' },
};

/**
 * The order fields read in, when an action names them. Anything the action asks
 * for that isn't here follows after, so a new field still renders.
 */
const FIELD_ORDER = [
	'carrier',
	'trackingNumber',
	'shippedAt',
	'estimatedDeliveryDate',
	'actualDeliveryDate',
];

/**
 * A field the API asks for that this form doesn't know about still has to be
 * fillable — otherwise `requiredFields` names something with no input, and the
 * form blocks on it forever. Renders as text, labelled from the field name.
 */
function metaFor(field: string) {
	return (
		FIELD_META[field] ?? {
			label: field
				.replace(/([A-Z])/g, ' $1')
				.replace(/^./, (c) => c.toUpperCase())
				.trim(),
			kind: 'text' as const,
		}
	);
}

export interface ShipmentAction {
	/** SHIP, DELIVER, CANCEL, EDIT_DETAILS. Branch on this, never on `label`. */
	key: string;
	/** Null on an action that writes fields without moving the shipment. */
	status?: OrderShipmentStatus | null;
	label: string;
	requiredFields: string[];
	optionalFields: string[];
	blocked?: boolean | null;
	blockedReason?: string | null;
}

export interface ShipmentForTracking {
	id: string;
	sequence: number;
	status: OrderShipmentStatus;
	carrier?: string | null;
	trackingNumber?: string | null;
	shippedAt?: string | null;
	estimatedDeliveryDate?: string | null;
	actualDeliveryDate?: string | null;
	availableActions?: ShipmentAction[] | null;
}

interface UpdateShipmentTrackingModalProps {
	shipment: ShipmentForTracking | null;
	/** Which action the card opened this for, by key. Falls back to the first
	 * unblocked one. */
	initialActionKey?: string | null;
	onClose: () => void;
	onSaved: () => void;
}

/**
 * Tracking for one shipment — the replacement for shipping an item at a time
 * once a shipment carries it.
 *
 * An action owns its fields. Everything rendered and everything submitted comes
 * from the chosen action's `requiredFields` + `optionalFields`, so the form can
 * neither offer nor send something the mutation refuses — a field an action
 * doesn't own is rejected outright. Nothing about the state machine, and no list
 * of which fields are writable when, is written down on this side.
 *
 * Correcting details is an action like any other (EDIT_DETAILS, `status: null`),
 * which is why a delivered or cancelled shipment still returns one. An empty
 * action list no longer means terminal.
 *
 * The carrier list is the picker's options, not a closed set — `carrier` is free
 * text server-side so local couriers can still be typed, and matching is lenient
 * so older rows resolve where they can. Picking from the list writes the stable
 * `code`, which is what lets the API build a tracking link.
 */
export function UpdateShipmentTrackingModal({
	shipment,
	initialActionKey,
	onClose,
	onSaved,
}: UpdateShipmentTrackingModalProps) {
	const { data: carrierData } = useGetShippingCarriersQuery({ fetchPolicy: 'no-cache' });
	const carriers = carrierData?.getShippingCarriers ?? [];

	const allActions = shipment?.availableActions ?? [];
	// The move is the caller's, not a choice made in here — both entry points take
	// a non-optional `actionKey` from the button that was pressed. The fallback
	// stands only for a caller that has none, and picks the first move the server
	// is not blocking rather than assuming one.
	const actionKey = initialActionKey ?? allActions.find((a) => !a.blocked)?.key ?? null;
	const [text, setText] = useState<Record<string, string>>({
		carrier: shipment?.carrier ?? '',
		trackingNumber: shipment?.trackingNumber ?? '',
	});
	const toDate = (iso?: string | null) => (iso ? new Date(iso) : null);
	const [dates, setDates] = useState<Record<string, Date | null>>({
		shippedAt: toDate(shipment?.shippedAt),
		estimatedDeliveryDate: toDate(shipment?.estimatedDeliveryDate),
		actualDeliveryDate: toDate(shipment?.actualDeliveryDate),
	});
	const [showErrors, setShowErrors] = useState(false);

	const [save, { loading }] = useUpdateOrderShipmentTrackingMutation();

	const chosen = allActions.find((a) => a.key === actionKey) ?? null;

	const missing = (chosen?.requiredFields ?? []).filter((f) =>
		metaFor(f).kind === 'date' ? !dates[f] : !text[f]?.trim()
	);

	// Exactly what the chosen action owns — nothing else is rendered, because
	// nothing else may be sent. FIELD_ORDER only decides the reading order.
	const fields = Array.from(
		new Set([...(chosen?.requiredFields ?? []), ...(chosen?.optionalFields ?? [])])
	).sort((a, b) => {
		const ia = FIELD_ORDER.indexOf(a);
		const ib = FIELD_ORDER.indexOf(b);
		return (ia === -1 ? FIELD_ORDER.length : ia) - (ib === -1 ? FIELD_ORDER.length : ib);
	});

	const handleSave = async () => {
		if (!shipment || !chosen) return;
		if (missing.length > 0) {
			setShowErrors(true);
			return;
		}
		try {
			// The API gates on a field being PRESENT in the input, not on its
			// value — sending one the action doesn't own reads as writing it and
			// the whole save is rejected, even as null.
			const detail = Object.fromEntries(
				fields.map((f) => [
					f,
					metaFor(f).kind === 'date'
						? (dates[f]?.toISOString() ?? null)
						: text[f]?.trim() || null,
				])
			);

			const { data } = await save({
				variables: {
					id: shipment.id,
					input: {
						// Null on EDIT_DETAILS — omitted so the shipment stays put.
						status: chosen.status ?? undefined,
						...detail,
					},
				},
			});
			const result = data?.updateOrderShipmentTracking;
			if (result?.success) {
				toast.success(result.message || 'Tracking updated');
				onSaved();
				onClose();
			} else {
				toast.error(result?.message || 'Could not update tracking.');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not update tracking.');
		}
	};

	return (
		<Dialog open={!!shipment} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-[600px]">
				<DialogHeader>
					{/* The move, then what it acts on. It used to read "Shipment 1
					    tracking" whatever you had clicked, so arriving from "Mark as
					    delivered" landed you on a heading for a different task. */}
					<DialogTitle>
						{chosen
							? `${chosen.label} · Shipment ${shipment?.sequence}`
							: `Shipment ${shipment?.sequence} tracking`}
					</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className="flex flex-col gap-4">
						{/* No action PICKER.
						    Every caller arrives having already chosen — both this page's
						    shipment strip and v1's shipments section take a non-optional
						    `actionKey` from the button that was pressed. Re-presenting
						    the choice made a radio group of one option: a control that
						    cannot be operated, asking for a decision already made.
						    The chosen move is in the title; this body is only its
						    fields. */}
						{allActions.length === 0 && (
							<div className="text-sm text-text-4">
								Nothing can be done to this shipment right now.
							</div>
						)}

						{fields.map((field) => {
							const meta = metaFor(field);
							const required = (chosen?.requiredFields ?? []).includes(field);
							const error =
								showErrors && missing.includes(field)
									? `Required to ${chosen?.label.toLowerCase()}`
									: undefined;
							return (
								<Field
									key={field}
									label={meta.label}
									required={required}
									error={error}
								>
									{meta.kind === 'date' ? (
										<DatePicker
											// A date set by mistake has to be removable:
											// the calendar has no deselect, so without
											// this the only way back is a value that is
											// wrong in a different way.
											clearable
											value={dates[field] ?? null}
											onChange={(d) =>
												setDates((prev) => ({
													...prev,
													[field]: d ?? null,
												}))
											}
										/>
									) : (
										<Input
											value={text[field] ?? ''}
											onChange={(e) =>
												setText((prev) => ({
													...prev,
													[field]: e.target.value,
												}))
											}
										/>
									)}
									{field === 'carrier' && carriers.length > 0 && (
										<div className="mt-1.5 flex flex-wrap gap-1.5">
											{carriers.map((c) => {
												const active =
													(text.carrier ?? '').trim().toLowerCase() ===
													c.code.toLowerCase();
												return (
													<button
														key={c.code}
														type="button"
														onClick={() =>
															setText((prev) => ({
																...prev,
																carrier: c.code,
															}))
														}
														title={
															c.supportsTracking
																? 'A tracking link resolves for this carrier'
																: 'No tracking page for this carrier'
														}
														className={cn(
															'h-6 rounded-md border px-2 text-xs',
															active ? 'text-text-1' : 'text-text-3'
														)}
														style={{
															borderColor: active
																? 'var(--accent)'
																: 'var(--line-2)',
															background: active
																? 'var(--accent-soft)'
																: 'transparent',
														}}
													>
														{c.name}
													</button>
												);
											})}
										</div>
									)}
								</Field>
							);
						})}
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					{/* Without an action there is nothing to submit — the action is
					    what names the fields. */}
					<Button onClick={handleSave} disabled={loading || !chosen}>
						{loading ? 'Saving…' : (chosen?.label ?? 'Save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
